import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from "@/assets/images/logo.png";
import { useLegaladviser } from '@/hooks/stores/useLegaladviser';
import { formService } from '@/services';
import { toast, Toaster } from 'react-hot-toast';
import { settingsService } from '@/services/settingsService';
import { Info } from 'lucide-react';
import InfoPopup from '@/pages/Users/Dynamic_Form_parent/InfoPopup';
import { questionnaireService } from '@/services/questionnaireService';

export function InitialQuestionnaire() {
  const navigate = useNavigate();
  const { userId } = useLegaladviser();

  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [answers, setAnswers] = useState({});
  const [questions, setQuestions] = useState([]);
  const [stepId, setStepId] = useState(null);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(7000);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentExampleAnswer, setCurrentExampleAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const textareaRef = useRef(null);
  const toastDisplayed = useRef(false); // Prevent duplicate toasts
   
  // Focus textarea when question changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [currentQuestion]);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setIsLoading(true);
        const data = await formService.getForm(2);
        const formSteps = data.data.form_form_steps || [];

        const extractedQuestions = formSteps.flatMap(step => {
          setStepId(step.id);
          return step.form_step_form_sections.flatMap(section =>
            section.form_step_form_fields.map(field => ({
              id: field.id,
              text: field.name,
              description: field.description || '',
              example_answer: field.example_answer || '',
              fieldType: field.field_type,
              sectionId: section.id
            }))
          );
        });

        // console.log("Extracted questions with example answers:", extractedQuestions);
        setQuestions(extractedQuestions);
      } catch (err) {
        console.error("Failed to fetch questions", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuestions();
  }, []);

  // Add new useEffect for fetching settings
  useEffect(() => {
    const initializeAutoSave = async () => {
      const timeout = await settingsService.getAutoSaveTimeout();
      setAutoSaveTimeout(timeout);
    };

    initializeAutoSave();
  }, []);

  const handleAnswerChange = (e) => {
    const newValue = e.target.value;
    const newAnswers = { ...answers, [currentQuestion]: newValue };
    setAnswers(newAnswers);

    if (typingTimeout) clearTimeout(typingTimeout);

    setTypingTimeout(setTimeout(() => {
      saveAnswer(newValue);
    }, autoSaveTimeout));
  };

  const saveAnswer = async (currentValue) => {
    const currentQ = questions[currentQuestion - 1];
    if (!currentQ || !stepId) return;

    const valueToSave = currentValue !== undefined ? currentValue : answers[currentQuestion] || '';

    const payload = {
      form_id: 2,
      step_id: stepId,
      user_id: userId,
      values: [
        {
          field_id: currentQ.id,
          section: currentQ.sectionId,
          section_repeat_index: 0,
          value: valueToSave
        }
      ],
      removed_sections: []
    };

    try {
      const responseData = await questionnaireService.saveStepData(payload);
      setLastSaved(new Date());

      // Prevent duplicate toast
      if (!toastDisplayed.current) {
        toastDisplayed.current = true;
        const timeOptions = { hour: '2-digit', minute: '2-digit' };
        toast.success(`Last saved at ${new Date().toLocaleTimeString([], timeOptions)}`, {
          duration: 4000,
          position: 'bottom-right',
          style: {
            background: '#FF6B4A',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            marginTop: '60px',
            fontWeight: '500',
          },
          iconTheme: {
            primary: '#fff',
            secondary: '#FF6B4A',
          },
        });

        setTimeout(() => {
          toastDisplayed.current = false; // Reset flag after toast disappears
        }, 7000);
      }

    } catch (error) {
      console.error("Error saving answer:", error);
    }
  };

  const handleNext = async () => {
    await saveAnswer();
    if (currentQuestion < questions.length) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleFinish = async () => {
    await saveAnswer();
    navigate('/questionnaire-complete');
  };

  useEffect(() => {
    return () => {
      if (typingTimeout) clearTimeout(typingTimeout);
    };
  }, [typingTimeout]);

  const currentQ = questions[currentQuestion - 1];

  const handleInfoClick = (exampleAnswer) => {
    setCurrentExampleAnswer(exampleAnswer);
    setIsPopupOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#eae5d6] flex flex-col p-8">
      {/* Progress Bar */}
      <div className="w-full max-w-3xl mx-auto mb-16">
        <div className="relative">
          <div className="h-[4px] bg-white w-full absolute top-1/2 -translate-y-1/2">
            <div 
              className="h-full bg-[#FF6B4A] transition-all duration-300"
              style={{ width: `${((currentQuestion - 1) / (questions.length - 1)) * 100}%` }}
            />
          </div>
          <div className="flex justify-between relative">
            {questions.map((_, index) => (
              <div 
                key={index}
                className={`w-4 h-4 rounded-full ${
                  index < currentQuestion ? 'bg-[#FF6B4A]' : 'bg-white'
                } transition-colors duration-300`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="flex-1 w-full max-w-3xl mx-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B4A]"></div>
          </div>
        ) : (
          currentQ && (
            <>
              <div className="relative">
                <div className="pr-8">
                  <h2 className="text-3xl font-medium text-gray-900 mb-4">{currentQ.text}</h2>
                </div>
                {currentQ.example_answer && (
                  <button
                    type="button"
                    onClick={() => handleInfoClick(currentQ.example_answer)}
                    className="absolute top-[0.8rem] right-0 text-[#FF6B4A] hover:text-[#ee623f] focus:outline-none focus:ring-0 focus:ring-[#FF6B4A] focus:ring-offset-2 rounded-full transition-colors"
                  >
                    <Info size={18} />
                  </button>
                )}
              </div>
              <p className="text-gray-600 mb-8">{currentQ.description}</p>

              {/* Input Area */}
              <textarea 
                ref={textareaRef}
                placeholder="Start typing..."
                className="w-full h-72 mb-10 border-none outline-none focus:ring-0 focus:outline-none text-gray-700 placeholder-gray-400 resize-none bg-white rounded-lg p-4"
                value={answers[currentQuestion] || ''}
                onChange={handleAnswerChange}
              />

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center">
                <button
                  onClick={handleBack}
                  className={`flex items-center text-[#FF6B4A] px-6 py-2 rounded-full border border-[#FF6B4A] hover:bg-[#FFF8F6] transition-colors ${
                    currentQuestion === 1 ? 'invisible' : ''
                  }`}
                >
                  ← Back
                </button>

                {currentQuestion < questions.length ? (
                  <button
                    onClick={handleNext}
                    disabled={!answers[currentQuestion] || answers[currentQuestion].trim() === ''}
                    className={`px-6 py-2 rounded-full transition-colors ${
                      !answers[currentQuestion] || answers[currentQuestion].trim() === ''
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-[#FF6B4A] text-white hover:bg-[#E55D3D]'
                    }`}
                  >
                    Next question →
                  </button>
                ) : (
                  <button
                    onClick={handleFinish}
                    disabled={!answers[currentQuestion] || answers[currentQuestion].trim() === ''}
                    className={`px-6 py-2 rounded-full transition-colors ${
                      !answers[currentQuestion] || answers[currentQuestion].trim() === ''
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-[#FF6B4A] text-white hover:bg-[#E55D3D]'
                    }`}
                  >
                    Finish
                  </button>
                )}
              </div>
            </>
          )
        )}
      </div>

      {/* Add InfoPopup component */}
      <InfoPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        exampleAnswer={currentExampleAnswer}
      />

      <div className="mt-auto pt-8 pb-16 flex justify-center">
        <img src={logo} alt="PostScrypt Logo" className="h-16 w-auto" />
      </div>

      <Toaster />
    </div>
  );
}